/* =============================================================================
 * Tabela de rotas.
 *
 * E' o indice do app: cada linha liga uma URL a um arquivo de src/pages. Uma
 * rota tem exatamente uma pagina, e uma pagina atende exatamente uma rota -
 * pagina nunca importa pagina. Cada uma monta o proprio cabecalho e faz as
 * proprias chamadas de API; ver docs/ARQUITETURA.md.
 *
 * A URL ESPELHA A PASTA. O caminho e' `/pasta/arquivo/params`, com uma unica
 * excecao: a lista mora na raiz da pasta, porque `/competicoes` e' o endereco
 * que a pessoa digita.
 *
 *   src/pages/competicoes/lista.js         ->  /competicoes
 *   src/pages/competicoes/classificacao.js ->  /competicoes/classificacao
 *   src/pages/gestao/elenco.js             ->  /gestao/elenco/:idClube
 *
 * Ir da URL ao arquivo (e do arquivo a' URL) passa a ser mecanico, sem
 * consultar esta tabela.
 *
 * A COMPETICAO NAO VIAJA NA URL. Ela e' o contexto em que o app inteiro esta',
 * escolhido uma vez e guardado no aparelho (shared/sessao/competicao-atual.js).
 * As telas de competicao ainda ACEITAM o id (`:id?`, opcional) para o link
 * compartilhado por fora continuar abrindo a competicao certa; quem entra por
 * ali tem a selecao trocada pela guarda `exigirCompeticao`. Os links que o
 * proprio app gera sao os curtos, sem id.
 *
 * Cada `carregar` e' um import() dinamico: o Vite gera um chunk por rota
 * automaticamente. `esqueleto` diz o que pintar enquanto o chunk viaja.
 *
 * Convencao que NAO muda: identidade do recurso vai no caminho, filtro
 * opcional vai na query. Trocar de categoria nao remonta a view - dispara
 * aoAtualizarQuery.
 * ========================================================================== */

import { exigirLogin, exigirCapacidade, exigirCompeticao, apenasVisitante, todas } from './guardas.js';

//As cinco telas de competicao entram pela mesma porta: id opcional na URL,
//selecao salva como padrao, e a lista como saida quando nao ha' nem um nem
//outro.
const daCompeticao = exigirCompeticao({ deParam: 'id' });

export const rotas = [
    // ---- publico ----------------------------------------------------------
    { padrao: '/', esqueleto: 'lista', carregar: () => import('@pages/home.js') },

    { padrao: '/competicoes', esqueleto: 'lista', carregar: () => import('@pages/competicoes/lista.js') },
    //A home e' onde se cai ao escolher uma competicao: identidade, menu de secoes
    //e os jogos de hoje e dos proximos dias.
    { padrao: '/competicoes/home/:id?', esqueleto: 'detalhe', guarda: daCompeticao, carregar: () => import('@pages/competicoes/home.js') },
    //Ocupa o lugar do antigo "Resumo": a mesma consulta, mostrando o que ela de
    //fato traz - as categorias, cada uma abrindo a classificacao dela.
    { padrao: '/competicoes/categorias/:id?', esqueleto: 'lista', guarda: daCompeticao, carregar: () => import('@pages/competicoes/categorias.js') },
    //Quem disputa a competicao, nas duas alturas em que se pergunta isso: o TIME
    //(a equipe de uma categoria, que e' quem entra em campo) e o CLUBE (a
    //agremiacao, que costuma ter um time em cada categoria). As duas sao publicas
    //e recortam em badges o que ja' esta' em maos - o filtro vive na query.
    //
    //Os clubes levam a /clubes/detalhe, que e' o clube ACIMA de qualquer
    //competicao; os times, a' vitrine /competicoes/time. A lista de times por
    //clube atras de login continua sendo /competicoes/clube-times.
    { padrao: '/competicoes/times/:id?', esqueleto: 'lista', guarda: daCompeticao, carregar: () => import('@pages/competicoes/times.js') },
    { padrao: '/competicoes/clubes/:id?', esqueleto: 'lista', guarda: daCompeticao, carregar: () => import('@pages/competicoes/clubes.js') },
    { padrao: '/competicoes/classificacao/:id?', esqueleto: 'tabela', guarda: daCompeticao, carregar: () => import('@pages/competicoes/classificacao.js') },
    //A terceira tela da familia de estatisticas, e a unica que COMPARA atletas:
    //artilheiros, goleiros menos vazados, os mais escalados, os mais advertidos e
    //um ranking para cada tipo de lance que a organizacao registrar (assistencia,
    //defesa...). Publica como as duas irmas - so' mostra o que ja' esta' na
    //sumula -, e cada nome dela abre /competicoes/atleta-estatisticas.
    //
    //O recorte e' a CATEGORIA, como a classificacao: Sub-10 e Sub-17 nao disputam
    //os mesmos gols. Ela viaja em `?cat=`, o criterio em `?r=` e a posicao em
    //`?pos=` - identidade no caminho, filtro na query.
    { padrao: '/competicoes/atletas-destaques/:id?', esqueleto: 'lista', guarda: daCompeticao, carregar: () => import('@pages/competicoes/atletas-destaques.js') },
    //A quinta da familia, e a de CIMA: o painel da competicao inteira - de que
    //tamanho ela e', quanto dela ja' foi jogado, o que saiu em campo e quem
    //apareceu. As outras quatro tem um sujeito DENTRO da competicao (o time, o
    //atleta, o jogo, a comparacao entre atletas); aqui o sujeito e' ela.
    //
    //Publica como as irmas - so' mostra o que a tabela pendurada no mural ja'
    //diz. O painel ADMINISTRATIVO da mesma competicao (conta corrente, valor de
    //inscricao, pendencia de pagamento) e' outra funcionalidade e nao tem tela
    //no Futigol.
    //
    //NAO E' ABA do cabecalho: a fileira ja' tem oito itens e rola no telefone, e
    //este e' um painel que se abre de vez em quando, nao a cada fim de semana. A
    //porta dele e' o menu da home - mesmo arranjo de `competicao-links`.
    { padrao: '/competicoes/competicao-estatisticas/:id?', esqueleto: 'detalhe', guarda: daCompeticao, carregar: () => import('@pages/competicoes/competicao-estatisticas.js') },
    { padrao: '/competicoes/rodadas/:id?', esqueleto: 'lista', guarda: daCompeticao, carregar: () => import('@pages/competicoes/rodadas.js') },
    //O mes inteiro numa grade - o irmao de /competicoes/clube-calendario com a
    //competicao no lugar do clube. E' o unico recorte em que passado e futuro
    //convivem: as tres telas de jogos sao listas de uma ponta do tempo cada.
    { padrao: '/competicoes/calendario/:id?', esqueleto: 'lista', guarda: daCompeticao, carregar: () => import('@pages/competicoes/calendario.js') },
    //Jogos sao tres recortes da mesma agenda, e cada recorte e' uma pagina: o
    //que vem pela frente, a agenda completa e o que ja' aconteceu. Quem liga as
    //tres e' o segmentado do cabecalho - ver cabecalho-competicao.js.
    { padrao: '/competicoes/jogos/:id?', esqueleto: 'lista', guarda: daCompeticao, carregar: () => import('@pages/competicoes/jogos.js') },
    { padrao: '/competicoes/jogos-marcados/:id?', esqueleto: 'lista', guarda: daCompeticao, carregar: () => import('@pages/competicoes/jogos-marcados.js') },
    { padrao: '/competicoes/jogos-realizados/:id?', esqueleto: 'lista', guarda: daCompeticao, carregar: () => import('@pages/competicoes/jogos-realizados.js') },
    //O ENDERECO ANTIGO do recorte acima, que so' redireciona - mesmo arranjo de
    ///partidas/detalhe mais abaixo, e pelo mesmo motivo: "manda o link dos
    //resultados" ja' circulou em grupo de WhatsApp, e link de competicao nao
    //expira. O `:id?` opcional viaja junto para o link que carregava a
    //competicao continuar caindo nela.
    { padrao: '/competicoes/jogos-resultados/:id?', esqueleto: 'lista', guarda: (ctx) => ({ redirecionar: ctx.params.id ? `/competicoes/jogos-realizados/${ctx.params.id}` : '/competicoes/jogos-realizados' }), carregar: () => import('@pages/competicoes/jogos-realizados.js') },
    { padrao: '/competicoes/regulamento/:id?', esqueleto: 'detalhe', guarda: daCompeticao, carregar: () => import('@pages/competicoes/regulamento.js') },

    //O que a organizacao divulga por fora do app - PDF, formulario, transmissao.
    //Chega pelo menu da home; os marcados como destaque viram atalho la'.
    { padrao: '/competicoes/competicao-links/:id?', esqueleto: 'lista', guarda: daCompeticao, carregar: () => import('@pages/competicoes/competicao-links.js') },

    //Edicao pela organizacao da liga. A guarda so' consegue cobrar login: quem
    //e' o dono da competicao esta' em EMAIL_ADMINISTRADOR, um campo que so'
    //existe depois do fetch - a permissao e' conferida dentro da pagina, com a
    //competicao ja' em maos. Ver o cabecalho de competicoes/competicao-edicao.js.
    { padrao: '/competicoes/competicao-edicao/:id?', esqueleto: 'formulario', guarda: todas(exigirLogin, daCompeticao), carregar: () => import('@pages/competicoes/competicao-edicao.js') },

    //A partida: a sumula inteira, e a saida para tudo o que se faz com ela.
    //
    //O `:id` aqui e' da PARTIDA, nao da competicao - por isso a guarda vai sem
    //`deParam` e so' cobra a selecao salva. Morava em /partidas/detalhe; ver o
    //cabecalho de competicoes/partida.js para o porque da mudanca.
    { padrao: '/competicoes/partida/:id', esqueleto: 'detalhe', guarda: exigirCompeticao(), carregar: () => import('@pages/competicoes/partida.js') },

    //O raio-x do jogo: o confronto somado, a campanha das duas equipes e o
    //retrospecto entre elas. PUBLICA como a partida, e util ANTES dela - por
    //isso nao exige nem placar nem sumula. E' o par de atleta-estatisticas, um
    //nivel ao lado.
    { padrao: '/competicoes/partida-estatisticas/:id', esqueleto: 'detalhe', guarda: exigirCompeticao(), carregar: () => import('@pages/competicoes/partida-estatisticas.js') },

    //A partida ACONTECENDO: o placar que anda e a linha do tempo que cresce.
    //
    //PUBLICA, como a partida e as estatisticas, e por uma razao de produto: a
    //tela ao vivo e' a que o grupo de WhatsApp do clube abre para acompanhar o
    //jogo. Quem opera (a liga, o dirigente do clube) ve' os mesmos numeros com
    //os botoes ao lado - e QUEM PODE MARCAR O QUE quem responde e' o servidor,
    //em ObterTabPartidaAoVivo, e nao uma guarda: depende do clube DA PARTIDA,
    //que so' se sabe depois do fetch. Mesmo arranjo de partida-nominata.
    { padrao: '/competicoes/partida-vivo/:id', esqueleto: 'detalhe', guarda: exigirCompeticao(), carregar: () => import('@pages/competicoes/partida-vivo.js') },

    //O ENDERECO ANTIGO da partida, que so' redireciona. Link de jogo e'
    //compartilhado no WhatsApp e nao expira - deixar o caminho velho cair no
    //"nao encontrado" quebraria toda conversa de grupo ja' enviada. A guarda
    //nunca deixa entrar, entao o `carregar` daqui jamais roda; ele existe porque
    //toda rota declara um.
    { padrao: '/partidas/detalhe/:id', esqueleto: 'detalhe', guarda: (ctx) => ({ redirecionar: `/competicoes/partida/${ctx.params.id}` }), carregar: () => import('@pages/competicoes/partida.js') },

    //As telas de gestao de UMA partida, abertas pelo container de acoes de
    //competicoes/partida.js. O `:id` aqui tambem e' da PARTIDA, e a guarda de
    //competicao vai sem `deParam` pelo mesmo motivo. A permissao de quem organiza
    //a liga e' conferida dentro de cada pagina, como na competicao-edicao acima.
    //
    //partida-edicao e' a UNICA das tres com `:id?`: sem id ela e' a partida NOVA,
    //e ai' a competicao so' pode ser a selecionada. As outras duas mexem em
    //momentos de um jogo que ja' existe, entao exigem o id.
    { padrao: '/competicoes/partida-edicao/:id?', esqueleto: 'formulario', guarda: todas(exigirLogin, exigirCompeticao()), carregar: () => import('@pages/competicoes/partida-edicao.js') },
    { padrao: '/competicoes/partida-previsao-edicao/:id', esqueleto: 'formulario', guarda: todas(exigirLogin, exigirCompeticao()), carregar: () => import('@pages/competicoes/partida-previsao-edicao.js') },
    { padrao: '/competicoes/partida-resultado-edicao/:id', esqueleto: 'formulario', guarda: todas(exigirLogin, exigirCompeticao()), carregar: () => import('@pages/competicoes/partida-resultado-edicao.js') },

    //A NEGOCIACAO da data, que nao e' a marcacao dela: os dois clubes propondo,
    //aceitando e fechando entre si, enquanto partida-previsao-edicao continua
    //sendo a liga decidindo sozinha. `lista` e nao `formulario` porque o que se
    //ve' ao chegar e' a conversa; o formulario e' um sheet dentro dela.
    //
    //Exige login porque toda acao aqui grava. QUEM pode fazer o que depende de ser
    //dirigente de um dos dois clubes (TAB_USUARIO_CLUBE) ou da organizacao da liga
    //(EMAIL_ADMINISTRADOR) - dois dados que so' existem DEPOIS do fetch, entao a
    //permissao e' conferida dentro da pagina, como nas tres rotas acima.
    { padrao: '/competicoes/partida-sugestoes/:id', esqueleto: 'lista', guarda: todas(exigirLogin, exigirCompeticao()), carregar: () => import('@pages/competicoes/partida-sugestoes.js') },

    //A PASTA DE DOCUMENTOS da partida: uma vaga por tipo que a competicao exige,
    //preenchida ou nao. O par da aba "Documentos" da sumula, que so' lista o que
    //ja' chegou - aqui a vaga VAZIA e' o assunto, porque a pergunta de quem abre
    //esta tela na vespera do jogo e' o que ainda falta.
    //
    //Exige login como a aba da sumula, e pelo mesmo motivo: ali moram nominatas
    //digitalizadas, com nome completo de menor de idade. QUEM ANEXA - a retaguarda,
    //o master e os dois clubes DO JOGO - e' conferido dentro da pagina, porque
    //quais sao esses dois clubes so' se sabe depois do fetch. Mesmo arranjo de
    //partida-nominata logo abaixo.
    { padrao: '/competicoes/partida-documentos/:id', esqueleto: 'lista', guarda: todas(exigirLogin, exigirCompeticao()), carregar: () => import('@pages/competicoes/partida-documentos.js') },

    //TROCAR O MANDANTE: quem joga em casa passa a visitante, e vice-versa. Uma
    //tela so' para isso porque a inversao e' UM gesto - nas telas de edicao ela
    //custaria dois seletores em sequencia, com os dois lados no mesmo clube no
    //meio do caminho.
    //
    //Mesma dupla de quem responde pela partida (retaguarda, master e os DOIS
    //clubes do jogo), conferida dentro da pagina pelo mesmo motivo das duas rotas
    //acima.
    { padrao: '/competicoes/partida-trocar-mandante/:id', esqueleto: 'detalhe', guarda: todas(exigirLogin, exigirCompeticao()), carregar: () => import('@pages/competicoes/partida-trocar-mandante.js') },

    //A nominata e' a terceira: e' de um TIME dentro de uma PARTIDA, e por isso e'
    //a unica com DOIS ids. Os segmentos `partida` e `time` sao LITERAIS, como o
    //`pessoa` da rota do atleta abaixo - dois numeros crus lado a lado nao dizem
    //qual e' qual. O clube nao entra no caminho: ele nao e' identidade do que se
    //ve' aqui, chega no `Time` da consulta, e e' la' que a pagina confere quem
    //pode montar a nominata.
    { padrao: '/competicoes/partida-nominata/partida/:idPartida/time/:idTime', esqueleto: 'lista', guarda: todas(exigirLogin, exigirCompeticao()), carregar: () => import('@pages/competicoes/partida-nominata.js') },

    //A area do clube DENTRO da competicao: o dirigente administrando o que e'
    //dele (atletas, times e transferencias) sem sair do contexto em que o app
    //inteiro esta'.
    //
    //O `:idClube` NAO e' a competicao - e' identidade de QUAL clube, e por isso
    //e' obrigatorio e nao aceita a forma `:id?` das telas publicas acima. A
    //competicao continua sendo a selecionada; quem nao tiver uma e' mandado
    //escolher no painel do proprio clube, e nao na lista publica, que so' mostra
    //as competicoes liberadas para o site.
    ...[
        { padrao: '/competicoes/clube-administracao/:idClube', esqueleto: 'detalhe', carregar: () => import('@pages/competicoes/clube-administracao.js') },
        { padrao: '/competicoes/clube-atletas/:idClube', esqueleto: 'lista', carregar: () => import('@pages/competicoes/clube-atletas.js') },
        //O calendario e' a UNICA das telas do clube que nao se limita a'
        //competicao selecionada: quem dirige o clube tem uma agenda so', e o
        //choque de datas mora justamente entre duas competicoes. Fica aqui mesmo
        //assim - a guarda e o cabecalho sao os das outras, e a competicao
        //continua sendo o contexto de onde se chegou.
        { padrao: '/competicoes/clube-calendario/:idClube', esqueleto: 'lista', carregar: () => import('@pages/competicoes/clube-calendario.js') },
        { padrao: '/competicoes/clube-times/:idClube', esqueleto: 'lista', carregar: () => import('@pages/competicoes/clube-times.js') },
        //As duas pontas da mesma negociacao: o que os outros clubes pediram deste
        //e o que este pediu dos outros. O pedido em si nasce em clube-atletas,
        //filtrando por atletas de outros clubes.
        { padrao: '/competicoes/clube-transferencias/:idClube', esqueleto: 'lista', carregar: () => import('@pages/competicoes/clube-transferencias.js') },
        //O time administrado pelo clube - o par da vitrine publica /competicoes/time.
        //Mesma guarda das outras quatro: dirigente do clube, retaguarda ou master.
        //Os ids seguem a hierarquia clube -> time -> pessoa, e cada caminho mais
        //longo estende o anterior.
        //
        //TRES TELAS, o mesmo corte que o clube tem um nivel acima: `time-gestao`
        //e' o inicio do time (identidade, numeros e atalhos), `time-atletas-gestao`
        //e' o elenco com as acoes em cima dos atletas, e `time-pessoa-inscricao` e'
        //UMA inscricao aberta por inteiro, com o historico dela. Ocupam o lugar da
        //antiga `time-administracao`, que era as tres coisas numa lista corrida.
        //
        //O sufixo `-gestao` no elenco separa das DUAS vitrines publicas de mesmo
        //nome logo abaixo (`time` e `time-atletas`): o par publico/administrado
        //existe para as duas telas, e sem o sufixo o nome do arquivo nao dizia de
        //qual lado da guarda ele estava.
        { padrao: '/competicoes/time-gestao/:idClube/:idTime', esqueleto: 'detalhe', carregar: () => import('@pages/competicoes/time-gestao.js') },
        { padrao: '/competicoes/time-atletas-gestao/:idClube/:idTime', esqueleto: 'lista', carregar: () => import('@pages/competicoes/time-atletas-gestao.js') },
        { padrao: '/competicoes/time-pessoa-inscricao/:idClube/:idTime/:idPessoa', esqueleto: 'detalhe', carregar: () => import('@pages/competicoes/time-pessoa-inscricao.js') },
    ].map((r) => ({
        ...r,
        guarda: todas(
            exigirCapacidade((cap, params) => cap.podeGerirClube(params.idClube)),
            exigirCompeticao({ escolherEm: (ctx) => `/clubes/clube-administracao/${ctx.params.idClube}` })
        ),
    })),

    //Cadastrar o atleta que ainda nao existe: localiza pelo CPF e, quando nao
    //acha ninguem, abre a ficha nova ja' vinculada a ESTE clube.
    //
    //Fica FORA do grupo acima porque a guarda e' outra: as tres telas do clube
    //pedem `podeGerirClube`, e aqui vale tambem para a RETAGUARDA da liga, que
    //nao dirige clube nenhum e mesmo assim digita a ficha que chegou em papel.
    //Ver o comentario de `podeInscreverAtleta` em sessao/capacidades.js.
    //
    //Nao aceita `:id?` de competicao pelo mesmo motivo das outras telas de
    //clube: o unico id do caminho e' o do CLUBE, e a competicao continua sendo a
    //selecionada - e' ela que decide as categorias, os documentos exigidos e se
    //a inscricao pelo site esta' aberta.
    //Procurar uma PESSOA no cadastro inteiro - por nome, CPF ou RG. Nao e' o
    //elenco de ninguem: e' a pergunta "esse atleta ja' existe em algum lugar?",
    //que vem antes de cadastrar alguem de novo. Por isso e' vizinha da rota
    //abaixo, e nao das telas de clube: as duas atendem o mesmo momento.
    //
    //O `:idClube?` E' OPCIONAL PORQUE A BUSCA NAO TEM DONO - ele existe so' para
    //o atalho "Cadastrar atleta", que precisa de um clube para abrir a ficha. Vem
    //preenchido quando se chega pela area do clube.
    //
    //A GUARDA E' A MESMA DO CADASTRO, e pelo mesmo motivo: a funcionalidade
    //devolve TAB_PESSOA crua (CPF, RG, nascimento), entao a tela nao e' publica
    //como as vitrines. Sem clube no caminho nao ha' o que testar com
    //`podeInscreverAtleta`, e o que sobra e' quem dirige clube ou e' da liga.
    {
        padrao: '/competicoes/pesquisar-atletas/:idClube?',
        esqueleto: 'formulario',
        guarda: todas(
            exigirCapacidade((cap, params) => (params.idClube ? cap.podeInscreverAtleta(params.idClube) : cap.ehDirigente || cap.ehRetaguarda)),
            exigirCompeticao({ escolherEm: (ctx) => (ctx.params.idClube ? `/clubes/clube-administracao/${ctx.params.idClube}` : '/competicoes') })
        ),
        carregar: () => import('@pages/competicoes/pesquisar-atletas.js'),
    },

    {
        padrao: '/competicoes/inscrever-atleta/:idClube',
        esqueleto: 'formulario',
        guarda: todas(
            exigirCapacidade((cap, params) => cap.podeInscreverAtleta(params.idClube)),
            exigirCompeticao({ escolherEm: (ctx) => `/clubes/clube-administracao/${ctx.params.idClube}` })
        ),
        carregar: () => import('@pages/competicoes/inscrever-atleta.js'),
    },

    //A ficha do atleta na competicao, aberta pela lista de atletas do clube.
    //
    //O SEGMENTO `pessoa` E' LITERAL, e nao sobra da tabela de rotas: o id aqui e'
    //de PESSOA (TAB_PESSOA), nao de atleta nem de inscricao - a mesma URL do
    //Futigol antigo, entao o link ja' compartilhado continua abrindo. De qual
    //clube se esta' olhando vai em `?clube=`, porque e' recorte e nao identidade.
    //
    //NAO EXIGE MAIS LOGIN, e a troca foi de porta por campo: a tela mostra CPF, RG
    //e nascimento - inclusive de menor -, e login nunca protegeu isso (qualquer
    //torcedor com conta lia tudo). Quem decide o que aparece e' o clube do atleta,
    //e a decisao mora no servidor, que devolve a ficha raspada para quem nao
    //responde por ele - ver ObterAtletaCompeticaoClubeDetalhe.cs. O que sobra para
    //o visitante e' o que a competicao ja' publica: nome, foto, idade, posicao,
    //clube, times e partidas.
    //
    //A competicao continua sendo a selecionada, como em /competicoes/partida - por
    //isso a guarda vai sem `deParam`.
    { padrao: '/competicoes/atleta/pessoa/:idPessoa', esqueleto: 'detalhe', guarda: exigirCompeticao(), carregar: () => import('@pages/competicoes/atleta.js') },

    //O par de escrita da ficha acima: corrigir o CADASTRO do atleta. Mesmo
    //endereco do Futigol antigo (/competicoes/atleta-edicao/pessoa/30964), mesmo
    //segmento literal `pessoa`, e o mesmo `?clube=` de recorte.
    //
    //A GUARDA NAO CONFERE PERMISSAO, e isso e' deliberado: quem pode editar
    //depende do clube DO ATLETA, que so' se sabe depois do fetch - e a guarda
    //roda antes. A rota cobra o que da' para cobrar aqui (login e competicao) e a
    //propria pagina explica quando nao da'. Mesmo arranjo de
    ///competicoes/competicao-edicao, pelo mesmo motivo.
    { padrao: '/competicoes/atleta-edicao/pessoa/:idPessoa', esqueleto: 'formulario', guarda: todas(exigirLogin, exigirCompeticao()), carregar: () => import('@pages/competicoes/atleta-edicao.js') },

    //O painel de numeros do mesmo atleta - o que ele fez em campo nesta
    //competicao. Mesmo segmento literal `pessoa` da ficha acima, pelo mesmo
    //motivo, e a mesma competicao selecionada.
    //
    //A DIFERENCA E' A GUARDA, e ela e' a razao de ser desta tela: a ficha mostra
    //CPF, RG e nascimento e exige login; esta so' mostra o que ja' esta' na
    //sumula - nome, foto, camisa, gols e cartoes -, entao e' publica, como a
    //vitrine do time. E' isso que permite a artilharia de
    //`time-estatisticas` finalmente clicar em cada nome sem prometer uma tela
    //de entrar. NAO sao um par com segmentado por isso mesmo: metade dos
    //visitantes nao abriria a outra metade.
    { padrao: '/competicoes/atleta-estatisticas/pessoa/:idPessoa', esqueleto: 'detalhe', guarda: exigirCompeticao(), carregar: () => import('@pages/competicoes/atleta-estatisticas.js') },

    //Vitrine publica do time - a administracao dele e' /competicoes/time-gestao,
    //com guarda. Vai sem `daCompeticao` porque o `:id` aqui e' do TIME, e a
    //competicao dele chega na propria consulta: nao ha' o que selecionar antes
    //de entrar.
    //
    //`time-atletas` e' a segunda metade da vitrine: quem joga no time. UM id, o
    //do TIME, e e' so' isso que a separa da homonima com guarda
    //(`time-atletas-gestao`, com clube e time) - o elenco publico nao precisa
    //saber de qual clube se esta' olhando, porque so' ha' um.
    //
    //`time-estatisticas` e' o painel de numeros do mesmo time: campanha, gols,
    //mando de campo, disciplina, artilharia e retrospecto por adversario. Ela e
    //`time` sao um PAR - mesmo cabecalho, ligadas pelo segmentado dele (ver
    //ui/componentes/cabecalho-time.js) -, e por isso a vitrine ficou so' com a
    //identidade e a agenda. Mesmo id e mesma ausencia de guarda das outras duas.
    //`time-calendario` e' a terceira do segmentado: o mes do time numa grade,
    //com o jogado e o por jogar juntos. O par dela um nivel acima e'
    //`clube-calendario`, que atravessa as competicoes do clube - aqui nao ha' o
    //que atravessar, e por isso ela fica sem guarda como as outras deste bloco.
    { padrao: '/competicoes/time/:id', esqueleto: 'detalhe', carregar: () => import('@pages/competicoes/time.js') },
    { padrao: '/competicoes/time-estatisticas/:id', esqueleto: 'detalhe', carregar: () => import('@pages/competicoes/time-estatisticas.js') },
    { padrao: '/competicoes/time-calendario/:id', esqueleto: 'lista', carregar: () => import('@pages/competicoes/time-calendario.js') },
    { padrao: '/competicoes/time-atletas/:id', esqueleto: 'lista', carregar: () => import('@pages/competicoes/time-atletas.js') },

    //O LOCAL - o campo, nao o jogo. Ele aparecia em toda tela de partida sem
    //levar a lugar nenhum; agora e' entidade com pagina propria, aberta pelo
    //link da ficha da partida.
    //
    //Um PAR ligado pelo segmentado do cabecalho, como o do time:
    //`local-partida` e' a identidade (onde fica, quem manda ali, o que vem e o
    //que passou) e `local-partida-calendario` e' o mes inteiro numa grade, que
    //e' onde se ve' se o campo esta' livre no domingo.
    //
    //Sem guarda e sem competicao no caminho: um campo nao pertence a nenhuma -
    //ele recebe todas. Mesmo grau de exposicao da pagina publica do clube.
    { padrao: '/competicoes/local-partida/:id', esqueleto: 'detalhe', carregar: () => import('@pages/competicoes/local-partida.js') },
    { padrao: '/competicoes/local-partida-calendario/:id', esqueleto: 'lista', carregar: () => import('@pages/competicoes/local-partida-calendario.js') },

    { padrao: '/clubes', esqueleto: 'lista', carregar: () => import('@pages/clubes/lista.js') },
    { padrao: '/clubes/detalhe/:id', esqueleto: 'detalhe', carregar: () => import('@pages/clubes/detalhe.js') },

    //A administracao do CLUBE INTEIRO, e nao a dele dentro de uma competicao -
    //por isso mora em /clubes, ao lado da pagina publica do mesmo clube, e nao
    //no bloco de /competicoes acima. A homonima de la'
    //(competicoes/clube-administracao) e' a mesma administracao um nivel abaixo,
    //ja' dentro da competicao escolhida.
    //
    //E' a UNICA tela do clube sem `exigirCompeticao`, e de proposito: e' aqui que
    //a competicao da gestao e' escolhida - exigir uma seria beco sem saida.
    { padrao: '/clubes/clube-administracao/:idClube', esqueleto: 'detalhe', guarda: exigirCapacidade((cap, params) => cap.podeGerirClube(params.idClube)), carregar: () => import('@pages/clubes/clube-administracao.js') },

    //O elenco do clube inteiro - TAB_CLUBE_ATLETA, e nao a inscricao numa
    //competicao. Saiu de dentro do painel acima, que voltou a ser painel: mesma
    //guarda e a mesma ausencia de `exigirCompeticao`, porque cadastro de clube
    //existe antes de haver competicao escolhida.
    { padrao: '/clubes/clube-elenco/:idClube', esqueleto: 'lista', guarda: exigirCapacidade((cap, params) => cap.podeGerirClube(params.idClube)), carregar: () => import('@pages/clubes/clube-elenco.js') },

    // ---- autenticacao -----------------------------------------------------
    //`apenasVisitante`: quem ja' entrou nao ve' o formulario de login de novo -
    //vai direto para onde queria ir. Ver a nota na guarda.
    { padrao: '/usuario/entrar', esqueleto: 'formulario', guarda: apenasVisitante, carregar: () => import('@pages/usuario/entrar.js') },
    { padrao: '/usuario/cadastrar-se', esqueleto: 'formulario', guarda: apenasVisitante, carregar: () => import('@pages/usuario/cadastrar-se.js') },
    { padrao: '/usuario/recuperar-senha', esqueleto: 'formulario', carregar: () => import('@pages/usuario/recuperar-senha.js') },

    // ---- autenticado ------------------------------------------------------
    { padrao: '/usuario/perfil', esqueleto: 'detalhe', guarda: exigirLogin, carregar: () => import('@pages/usuario/perfil.js') },
    //Favoritos ficam no aparelho: funciona deslogado, e por isso nao tem guarda.
    { padrao: '/usuario/favoritos', esqueleto: 'lista', carregar: () => import('@pages/usuario/favoritos.js') },
    { padrao: '/usuario/atletas', esqueleto: 'lista', guarda: exigirLogin, carregar: () => import('@pages/usuario/atletas.js') },

    //O modo offline: o consentimento de guardar conteudo neste aparelho e o que
    //ja' foi baixado. Sem guarda pelo mesmo motivo dos favoritos, e com um a
    //mais: o Futigol e' publico, e quem acompanha a competicao do bairro num
    //ginasio sem sinal precisa do offline tanto quanto quem dirige um clube - o
    //pacote de navegacao tem uma porta para o visitante deslogado justamente por
    //isso (ver shared/offline/pacote-navegacao.js).
    //
    //E' para ca' que aponta a faixa "sem conexao" da casca (app/shell/app-shell.js).
    { padrao: '/usuario/offline', esqueleto: 'detalhe', carregar: () => import('@pages/usuario/offline.js') },

    //A OUTRA tela "deste aparelho", irma da de cima: tirar o Futigol da aba do
    //navegador e po-lo na tela de inicio. Sem guarda pelo mesmo motivo do
    //offline e dos favoritos - instalar e' do aparelho, nao da conta, e o
    //visitante deslogado e' justamente quem mais ganha com o icone na tela.
    //
    //Ela nao depende de nada remoto: tudo o que mostra vem do navegador (o
    //convite guardado e o user agent). Ver shared/instalacao/instalacao.js.
    { padrao: '/usuario/instalar', esqueleto: 'detalhe', carregar: () => import('@pages/usuario/instalar.js') },

    // ---- perfil clube -----------------------------------------------------
    // Todas passam pela mesma guarda: precisa poder gerir ESTE clube.
    // Lembrando que isso e' UX - a restricao real tem que estar no backend.
    //
    // A gestao acontece DENTRO de uma competicao, mas a competicao e' a
    // selecionada - a URL carrega so' a hierarquia que sobrou (clube -> time ->
    // partida), e um caminho mais longo continua estendendo o anterior. Quem nao
    // tiver competicao escolhida e' mandado escolher em
    // /clubes/clube-administracao, e nao na lista publica.
    ...[
        { padrao: '/gestao/clube-competicao/:idClube', esqueleto: 'lista', carregar: () => import('@pages/gestao/clube-competicao.js') },
        { padrao: '/gestao/elenco/:idClube', esqueleto: 'lista', carregar: () => import('@pages/gestao/elenco.js') },
    ].map((r) => ({
        ...r,
        guarda: todas(
            exigirCapacidade((cap, params) => cap.podeGerirClube(params.idClube)),
            exigirCompeticao({ escolherEm: (ctx) => `/clubes/clube-administracao/${ctx.params.idClube}` })
        ),
    })),

    // ---- fallback ---------------------------------------------------------
    { padrao: '*', esqueleto: 'detalhe', carregar: () => import('@pages/nao-encontrado.js') },
];
